import { ReactNode } from "react";
import { Loader2, Pencil, type LucideIcon } from "lucide-react";

import { cn } from "@/lib/utils";

interface SidebarHighlightCardProps {
  icon: LucideIcon;
  /** Texto antes do "•" — ex.: "Visibilidade", "Prazo". */
  title: string;
  /** Texto depois do "•" — ex.: "Privada", "No Prazo". */
  tag: string;
  /** Valor em destaque (resumo, data). */
  value: ReactNode;
  /** Linha de apoio abaixo do valor. */
  description?: ReactNode;
  /** Cor do título/ícone. */
  color: string;
  /** Fundo + borda do card. */
  bg: string;
  /** Cor do valor (default: foreground). */
  valueClassName?: string;
  editable?: boolean;
  isLoading?: boolean;
}

/**
 * Card de destaque da sidebar — base visual compartilhada entre os campos
 * editáveis "em destaque" (visibilidade e prazo), garantindo mesmo padding,
 * altura mínima e a mesma affordance de edição (lápis no hover).
 */
export function SidebarHighlightCard({
  icon: Icon,
  title,
  tag,
  value,
  description,
  color,
  bg,
  valueClassName,
  editable = false,
  isLoading = false,
}: SidebarHighlightCardProps) {
  return (
    <div
      className={cn(
        "group relative flex min-h-[5.25rem] flex-col gap-1.5 rounded-lg border p-3.5 text-left transition-all",
        bg,
        editable && !isLoading && "cursor-pointer hover:bg-accent",
      )}
    >
      <span
        className={cn(
          "flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider",
          color,
        )}
      >
        <Icon className="h-3.5 w-3.5" />
        {title} • {tag}
      </span>

      <p
        className={cn(
          "flex items-center gap-2 text-sm font-semibold text-foreground",
          valueClassName,
        )}
      >
        {value}
        {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      </p>

      {description && <p className="text-xs leading-snug text-muted-foreground">{description}</p>}

      {editable && !isLoading && (
        <span
          className={cn(
            "absolute right-2 top-2 rounded-md p-1.5 opacity-0 transition-opacity group-hover:opacity-100",
            bg,
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
        </span>
      )}
    </div>
  );
}
